import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Flag, Loader2 } from "lucide-react";

import { REPORT_CATEGORIES } from "../../constants/reportOptions";
import { useSubmitReport } from "../../hooks/queries/useReports";

const INITIAL_FORM = {
  category: "",
  description: "",
};

const ReportModal = ({
  isOpen,
  onClose,
  reportedAthleteId,
  reportedScoutId,
  reportedName,
  context,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const submitReport = useSubmitReport();

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setFormData(INITIAL_FORM);
    setError("");
    onClose();
  };

  const isValid = formData.category.trim();

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    setError("");

    try {
      await submitReport.mutateAsync({
        reportedAthleteId: reportedAthleteId || null,
        reportedScoutId: reportedScoutId || null,
        category: formData.category,
        description: formData.description || null,
        context,
      });

      setFormData(INITIAL_FORM);
      onClose();
    } catch (err) {
      setError(err.message || "No se pudo enviar el reporte.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <div className="flex items-center gap-2">
                  <Flag size={20} className="text-red-600" />
                  <h2 className="text-xl font-semibold uppercase tracking-wide text-gray-900">
                    Reportar usuario
                  </h2>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Reportar a {reportedName}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Cerrar"
              >
                <X size={20} className="text-gray-700" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo del reporte*
                </label>
                <div className="relative">
                  <select
                    value={formData.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 appearance-none bg-white"
                  >
                    <option value="">Selecciona un motivo</option>
                    {REPORT_CATEGORIES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Describe el problema (opcional)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 placeholder-gray-400 resize-none"
                />
                <div className="flex justify-end mt-1">
                  <span className="text-xs text-gray-400">
                    {formData.description.length}/500
                  </span>
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={!isValid || isSubmitting}
                className="inline-flex items-center justify-center gap-2 bg-red-600 px-8 py-3 text-xs font-bold uppercase tracking-[0.3em] text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 rounded-lg"
              >
                {isSubmitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Flag size={16} />
                )}
                {isSubmitting ? "Enviando..." : "Enviar reporte"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportModal;
